import React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Konfirmasi password tidak cocok."); return; }
    if (password.length < 8) { setErr("Password minimal 8 karakter."); return; }
    if (!token) { setErr("Token tidak valid."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password berhasil diubah");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Reset gagal. Token mungkin sudah kedaluwarsa.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/40" data-testid="reset-password-page">
      <Helmet><title>Reset Password — BATIK</title></Helmet>
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 lg:p-10 shadow-xl">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="font-serif text-2xl text-primary mb-3">Password Berhasil Diubah</h1>
            <p className="text-sm text-muted-foreground mb-6">Anda akan dialihkan ke halaman login...</p>
            <Link to="/login" className="text-accent underline text-sm">Login Sekarang</Link>
          </div>
        ) : (
          <>
            <div className="h-12 w-12 rounded-full bg-accent/15 flex items-center justify-center mb-5">
              <Lock className="h-5 w-5 text-accent" />
            </div>
            <p className="overline mb-2">Reset Password</p>
            <h1 className="font-serif text-3xl text-primary tracking-tight leading-tight mb-6">Buat Password Baru</h1>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password Baru</label>
                <div className="mt-1.5 relative">
                  <input data-testid="reset-password" type={showPw ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-3 pr-10 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Minimal 8 karakter" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Konfirmasi Password Baru</label>
                <input data-testid="reset-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Ulangi password baru" autoComplete="new-password" />
              </div>
              {err && <p className="text-xs text-red-600" data-testid="reset-error">{err}</p>}
              <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Reset Password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
